<template>
  <v-container fluid class="po-create-page pa-0">
    <v-alert
      v-if="selectedVendor"
      border="left"
      type="error"
      transition="slide-y-transition"
      dismissible
      class="mb-1"
      v-model="selectedVendor.alert"
      color="red"
    >
      <strong>{{ selectedVendor.note }}</strong>
    </v-alert>
    <v-card outlined class="po-info-card">
      <div class="po-header">
        <div class="title-wrap">
          <v-btn icon small class="mr-2" @click="cancel"><v-icon>mdi-chevron-left</v-icon></v-btn>
          <div class="page-title">Create Purchase Order</div>
        </div>
        <div class="header-actions">
          <v-btn v-if="!$vuetify.breakpoint.xsOnly" small rounded text @click.stop.prevent="cancel">Cancel</v-btn>
          <v-btn color="primary" rounded depressed dark :loading="loading"
            :disabled="PurchaseOrder.PurchaseOrderDetails.length === 0" @click="savePurchaseOrder">
            <v-icon left small>mdi-content-save-outline</v-icon>
            Save {{ invoiceTotal | currency }}
          </v-btn>
        </div>
      </div>
      <v-card-text class="pa-3">
        <v-row dense>
              <v-col cols="12" md="7">
                <div class="field-label">Vendor</div>
                <AutoComplete
                  v-model="selectedVendor"
                  :errorMessages="selectVendorError"
                />
              </v-col>
              <v-col cols="12" sm="8" md="4">
                <div class="field-label">Order Date</div>
                <DateSelector label="Date" v-model="PurchaseOrder.date" />
              </v-col>
              <v-col cols="12" sm="4" md="1" class="note-column">
                <v-dialog v-model="orderNote" width="500">
                  <template v-slot:activator="{ on }">
                    <v-btn block outlined rounded color="primary" height="40" v-on="on">
                      <v-icon :left="$vuetify.breakpoint.mdAndDown" small>mdi-note-text-outline</v-icon>
                      <span v-if="$vuetify.breakpoint.mdAndDown">Note</span>
                    </v-btn>
                  </template>

                  <v-card class="note-dialog-card">
                    <div class="dialog-header">
                      <div class="dialog-title">Purchase Order Note</div>
                      <v-btn icon small @click="orderNote = false"><v-icon>mdi-close</v-icon></v-btn>
                    </div>
                    <v-card-text class="pa-4">
                      <div class="field-label">Note</div>
                      <v-textarea
                        outlined
                        dense
                        hide-details
                        v-model="PurchaseOrder.note"
                        label="Purchase Order Note"
                      ></v-textarea>
                    </v-card-text>

                    <v-divider />

                    <v-card-actions class="dialog-actions">
                      <v-spacer />
                      <v-btn color="primary" rounded depressed dark @click="orderNote = false">Done</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <div class="po-create-content">
      <div>
        <ProductSearchBar @submit="addRow" />
      </div>
      <div>
        <v-card outlined class="po-items-card">
          <v-card-text class="pa-4">
            <div class="items-header">
              <div class="items-title">Purchase Order Items</div>
              <div class="item-metrics">
                <div class="metric"><strong>{{ PurchaseOrder.PurchaseOrderDetails.length }}</strong><span>Lines</span></div>
                <div class="metric"><strong>{{ totalQty }}</strong><span>Quantity</span></div>
              </div>
            </div>
            <v-data-table
              ref="purchaseOrderTable"
              :mobile-breakpoint="0"
              :headers="headers"
              :items="PurchaseOrder.PurchaseOrderDetails"
              hide-default-footer
              no-data-text="No item on this order yet"
              disable-pagination
              :items-per-page="-1"
              class="po-items-table"
            >
              <template v-slot:item="{ item, index }">
                <tr :key="item.id">
                  <td>
                    <v-layout align-center justify-start row fill-height wrap>
                      <v-btn icon @click="deleteItem(index)">
                        <v-icon color="red">mdi-delete-outline</v-icon>
                      </v-btn>
                      <v-text-field
                        v-model="item.name"
                        :disabled="lockProductName"
                        outlined
                        hide-details
                        @focus="$event.target.select()"
                        dense
                      ></v-text-field>
                    </v-layout>
                  </td>
                  <td class="text-center">
                    <v-text-field
                      v-model="item.qty"
                      :disabled="item.imeiNumber ? true : false"
                      outlined
                      class="centered-input"
                      hide-details
                      dense
                      @focus="$event.target.select()"
                    ></v-text-field>
                  </td>
                  <td class="text-right">
                    <v-text-field
                      v-model="item.price"
                      outlined
                      class="al-r"
                      hide-details
                      @focus="$event.target.select()"
                      dense
                    ></v-text-field>
                  </td>
                  <td class="text-right">
                    {{ (item.qty * item.price) | currency }}
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-card-text>
          <v-card v-if="totalQty > 0" flat class="items-total-card">
            <v-card-text>
              <div class="text-left">
                <span class="font-weight-medium">{{ totalQty }}</span> Total
                Item
              </div>
            </v-card-text>
          </v-card>
        </v-card>
      </div>
    </div>
  </v-container>
</template>

<script>
const { validationMixin } = require("vuelidate");
const { required, minValue } = require("vuelidate/lib/validators");
import DateSelector from "@/components/DateSelector";
import AutoComplete from "@/modules/Inventory/Vendor/components/AutoComplete";
import ProductSearchBar from "@/modules/Inventory/PurchaseOrder/components/ProductSearchBar";
import orderService from "../service";
import { mapGetters } from "vuex";

export default {
  name: "create-purchase-order",
  data() {
    return {
      orderNote: false,
      selectedVendor: null,
      Vendor: [],
      loading: false,
      PurchaseOrder: {
        date: this.$moment().toISOString(),
        note: null,
        VendorId: null,
        PurchaseOrderDetails: [],
      },
      headers: [
        {
          text: "Name",
          align: "left",
          value: "name",
          sortable: true,
        },
        {
          text: "Quantity",
          align: "center",
          value: "qty",
          width: "100",
          sortable: true,
        },
        {
          text: "Price",
          align: "right",
          value: "price",
          sortable: true,
        },
        {
          text: "Total",
          align: "right",
          value: "total",
          sortable: true,
        },
      ],
    };
  },
  components: {
    DateSelector,
    AutoComplete,
    ProductSearchBar,
  },
  mixins: [validationMixin],
  validations: {
    PurchaseOrder: {
      VendorId: {
        required,
        minValue: minValue(1),
      },
    },
  },
  computed: {
    ...mapGetters("global", ["currentBusiness"]),
    lockProductName() {
      if (!this.currentBusiness.metadata.hasOwnProperty("lockProductName")) {
        return true;
      } else if (this.currentBusiness.metadata.lockProductName) {
        return true;
      }
      return false;
    },
    totalQty() {
      let total = 0;
      if (this.PurchaseOrder.PurchaseOrderDetails) {
        this.PurchaseOrder.PurchaseOrderDetails.map((row) => {
          total += parseFloat(row.qty);
        });
      }

      return total;
    },
    selectVendorError() {
      const errors = [];
      if (!this.$v.PurchaseOrder.VendorId.$dirty) return errors;
      if (!this.$v.PurchaseOrder.VendorId.required)
        errors.push("Pleaes select vendor");
      if (!this.$v.PurchaseOrder.VendorId.minValue)
        errors.push("Pleaes select vendor");
      return errors;
    },
    invoiceTotal() {
      let total = 0;
      this.PurchaseOrder.PurchaseOrderDetails.map((row) => {
        total += row.total;
      });
      return total;
    },
  },
  watch: {
    selectedVendor() {
      this.PurchaseOrder.VendorId = this.selectedVendor.id;
    },
  },
  methods: {
    cancel() {
      this.$router.go(-1);
    },
    savePurchaseOrder() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });
      } else {
        if (this.PurchaseOrder.PurchaseOrderDetails.length > 0) {
          return orderService.create(this.PurchaseOrder).then(() => {
            // console.log("result", result);
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "success",
                title: "Purchase Order was created!",
              });

            this.$router.go(-1);
          });
        }
      }
    },
    deleteItem(i) {
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      }).then((result) => {
        if (result.value) {
          this.PurchaseOrder.PurchaseOrderDetails.splice(i, 1);
        }
      });
    },

    addRow(val) {
      // console.log("addrow", val);
      this.PurchaseOrder.PurchaseOrderDetails.push({
        ProductId: val.id,
        name: val.Bar.name,
        price: val.Bar.price,
        total: val.Bar.price * val.Bar.qty,
        qty: val.Bar.qty,
      });
    },
  },
};
</script>

<style scoped>
.po-create-page{background:#f8fafc}.po-info-card,.po-items-card{border-radius:20px;background:#fff;overflow:hidden}.po-header{padding:16px;border-bottom:1px solid #e2e8f0;display:flex;align-items:center;justify-content:space-between;gap:16px;flex-wrap:wrap}.title-wrap{display:flex;align-items:center}.page-title{color:#0f172a;font-size:22px;font-weight:800}.header-actions{display:flex;align-items:center;gap:8px}.field-label{color:#475569;font-size:12px;font-weight:700;margin-bottom:6px}.note-column{display:flex;align-items:flex-end;padding-bottom:6px}.po-create-content{display:grid;gap:14px;margin-top:14px}.note-dialog-card{border-radius:20px;overflow:hidden}.dialog-header{padding:18px 20px;border-bottom:1px solid #e2e8f0;display:flex;align-items:center;justify-content:space-between}.dialog-title,.items-title{color:#0f172a;font-size:18px;font-weight:800}.dialog-actions{padding:12px 16px}.items-header{display:flex;align-items:center;justify-content:space-between;gap:16px;margin-bottom:14px}.item-metrics{display:flex;gap:8px}.metric{min-width:72px;background:#f8fafc;border-radius:13px;padding:8px 11px;text-align:center}.metric strong{display:block;color:#0f172a;font-size:17px}.metric span{display:block;color:#64748b;font-size:9px;text-transform:uppercase}.po-items-table{border:1px solid #e2e8f0;border-radius:16px;overflow:hidden}.items-total-card{background:#f8fafc;border-top:1px solid #e2e8f0}::v-deep .po-items-table tbody tr:hover{background:#f8fafc}@media(max-width:600px){.po-header{padding:14px}.header-actions{width:100%}.header-actions .v-btn:last-child{flex:1}.items-header{align-items:flex-start;flex-direction:column}.item-metrics{width:100%}.metric{flex:1}}
</style>
